import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Image,
  IconButton,
  Flex,
  Text,
  useBreakpointValue,
  Container,
  Heading,
  VStack,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

const ResponsiveImageSliderWithHeader = ({
  images = [],
  autoPlayInterval = 2000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const autoPlayTimer = useRef(null);
  const pauseTimer = useRef(null);

  const sliderHeight = useBreakpointValue({
    base: "200px",
    md: "300px", 
    lg: "400px",
    xl: "450px",
  });

  const buttonSize = useBreakpointValue({
    base: "sm",
    md: "md",
    lg: "lg",
  });

  // All your existing timer management functions remain the same
  const clearAllTimers = useCallback(() => {
    if (autoPlayTimer.current) {
      clearInterval(autoPlayTimer.current);
      autoPlayTimer.current = null;
    }
    if (pauseTimer.current) {
      clearTimeout(pauseTimer.current);
      pauseTimer.current = null;
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    if (!images || images.length <= 1) return;
    clearAllTimers();
    autoPlayTimer.current = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, autoPlayInterval);
  }, [images?.length, autoPlayInterval, clearAllTimers]);

  const stopAutoPlay = useCallback(() => {
    clearAllTimers();
  }, [clearAllTimers]);

  const pauseTemporarily = useCallback(
    (duration = 5000) => {
      setIsPaused(true);
      stopAutoPlay();
      if (pauseTimer.current) {
        clearTimeout(pauseTimer.current);
      }
      pauseTimer.current = setTimeout(() => {
        setIsPaused(false);
      }, duration);
    },
    [stopAutoPlay]
  );

  // All your existing useEffect hooks remain the same
  useEffect(() => {
    if (!images || images.length <= 1 || isPaused || isHovered) {
      stopAutoPlay();
      return;
    }
    startAutoPlay();
    return () => {
      clearAllTimers();
    };
  }, [
    images?.length,
    autoPlayInterval,
    isPaused,
    isHovered,
    startAutoPlay,
    stopAutoPlay,
    clearAllTimers,
  ]);

  useEffect(() => {
    if (!isPaused && !isHovered && images && images.length > 1) {
      startAutoPlay();
    }
  }, [isPaused, isHovered, images?.length, startAutoPlay]);

  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  // Navigation functions
  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
    pauseTemporarily();
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
    pauseTemporarily();
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    pauseTemporarily();
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  if (!images || images.length === 0) {
    return (
      <Container maxW="container.xl" p={0}>
        <VStack spacing={6}>
          {/* Header Section */}
          <Box
            width="100%"
            textAlign="center"
            py={{ base: 6, md: 8, lg: 10 }}
            px={{ base: 4, md: 6 }}
          >
            <Heading
              as="h1"
              size={{ base: "xl", md: "2xl", lg: "3xl" }}
              color="#000B8C"
              fontWeight="bold"
              mb={4}
            >
              Where Innovation{" "}
              <Text as="span" color="gray.700">
                Happens
              </Text>
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg", lg: "xl" }}
              color="gray.600"
              maxW="4xl"
              mx="auto"
              lineHeight="1.6"
            >
              From collaborative workspaces to peaceful retreat areas, every
              space is designed to inspire creativity and support your best
              work.
            </Text>
          </Box>

          {/* No Images Message */}
          <Box
            height={sliderHeight}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="gray.100"
            borderRadius="lg"
            width="100%"
          >
            <Text color="gray.500" fontSize="lg">
              No images available
            </Text>
          </Box>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" p={0}>
      <VStack spacing={6}>
        {/* Header Section */}
        <Box
          width="100%"
          textAlign="center"
          py={{ base: 6, md: 8, lg: 10 }}
          px={{ base: 4, md: 6 }}
        >
          <Text
            fontSize={{ base: "xl", md: "2xl", lg: "4xl" }}
            color="#000B8C"
            fontWeight="bold"
            mb={4}
          >
            Where Innovation{" "}
            <Text as="span" color="gray.700">
              Happens
            </Text>
          </Text>
          <Text
            fontSize={{ base: "md", md: "lg", lg: "xl" }}
            color="gray.600"
            maxW="4xl"
            mx="auto"
            lineHeight="1.6"
          >
            From collaborative workspaces to peaceful retreat areas, every space
            is designed to inspire creativity and support your best work.
          </Text>
        </Box>

        {/* Image Slider */}
        <Box
          position="relative"
          height={sliderHeight}
          overflow="hidden"
          borderRadius="lg"
          width="100%"
          maxW={{ base: "100%", md: "800px", lg: "900px", xl: "1000px" }}
          mx="auto"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Image Display */}
          <Box position="relative" width="100%" height="100%">
            {images.map((image, index) => (
              <Image
                key={index}
                src={image.src}
                alt={image.alt || `Slider image ${index + 1}`}
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                objectFit="cover"
                opacity={currentIndex === index ? 1 : 0}
                transition="opacity 0.5s ease-in-out"
                zIndex={currentIndex === index ? 1 : 0}
              />
            ))}
          </Box>

          {/* Navigation Arrows - Only show if multiple images */}
          {images.length > 1 && (
            <>
              <IconButton
                icon={<ChevronLeftIcon />}
                position="absolute"
                left={{ base: 2, md: 4 }}
                top="50%"
                transform="translateY(-50%)"
                size={buttonSize}
                colorScheme="blackAlpha"
                variant="solid"
                onClick={goToPrevious}
                zIndex={2}
                bg="blackAlpha.600"
                color="white"
                _hover={{
                  bg: "blackAlpha.800",
                  transform: "translateY(-50%) scale(1.1)",
                }}
              />

              <IconButton
                icon={<ChevronRightIcon />}
                position="absolute"
                right={{ base: 2, md: 4 }}
                top="50%"
                transform="translateY(-50%)"
                size={buttonSize}
                colorScheme="blackAlpha"
                variant="solid"
                onClick={goToNext}
                zIndex={2}
                bg="blackAlpha.600"
                color="white"
                _hover={{
                  bg: "blackAlpha.800",
                  transform: "translateY(-50%) scale(1.1)",
                }}
              />
            </>
          )}

          {/* Dots Indicator */}
          {images.length > 1 && (
            <Flex
              position="absolute"
              bottom={{ base: 2, md: 4 }}
              left="50%"
              transform="translateX(-50%)"
              gap={{ base: 1, md: 2 }}
            >
              {images.map((_, index) => (
                <Box
                  key={index}
                  width={{ base: "8px", md: "12px" }}
                  height={{ base: "8px", md: "12px" }}
                  borderRadius="full"
                  bg={currentIndex === index ? "white" : "whiteAlpha.500"}
                  cursor="pointer"
                  onClick={() => goToSlide(index)}
                  transition="all 0.3s"
                  _hover={{ bg: "white", transform: "scale(1.2)" }}
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                />
              ))}
            </Flex>
          )}

          {/* Gradient Overlay */}
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height="30%"
            bgGradient="linear(to-t, blackAlpha.600, transparent)"
            pointerEvents="none"
          />

          {/* Image Counter */}
          <Box
            position="absolute"
            top={{ base: 2, md: 4 }}
            right={{ base: 2, md: 4 }}
            bg="blackAlpha.600"
            color="white"
            px={3}
            py={1}
            borderRadius="md"
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="medium"
          >
            {currentIndex + 1} / {images.length}
          </Box>

          {/* Auto-play indicator */}
          <Box
            position="absolute"
            top={{ base: 2, md: 4 }}
            left={{ base: 2, md: 4 }}
            bg={isPaused || isHovered ? "red.500" : "green.500"}
            width="8px"
            height="8px"
            borderRadius="full"
            opacity={0.7}
          />
        </Box>
        <Box mt={10}> </Box>
      </VStack>
    </Container>
  );
};

export default ResponsiveImageSliderWithHeader;
